import type {
  AnySet, DemandeSet, DevSet, Family, ImportanceSet, PhasageSet, Plan, PlanResults, PointsDepartSet, TraceSet,
} from './types';

// Modèle simplifié (non métier) alimenté par les bâtiments de gentilly.json (proposition-c, non modifié).

export interface BuildingRow {
  id: string;
  heat: number;
  heatKnown: boolean;
  address: string;
  usage: string;
  area?: number;
  heatingEnergy: string;
}

interface CompactBuildings {
  addressSuffix: string;
  dict: Record<string, string[]>;
  buildings: { fields: string[]; rows: (string | number | null)[][] };
}

let BUILDINGS: BuildingRow[] = [];

export async function loadBuildings(): Promise<void> {
  if (BUILDINGS.length) return;
  const c = (await import('../proposition-c/data/gentilly.json')).default as unknown as CompactBuildings;
  const { fields, rows } = c.buildings;
  const iId = fields.indexOf('functionalId');
  const iHeat = fields.indexOf('heat');
  const iAddr = fields.indexOf('address');
  const iUsage = fields.indexOf('usageLabel');
  const iUsageCategory = fields.indexOf('usage');
  const lookup = (field: string, value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return field === 'address' ? 'Adresse inconnue' : 'Non renseigné';
    return typeof value === 'number' ? c.dict[field]?.[value] ?? 'Non renseigné' : value;
  };
  const address = (value: string | number | null | undefined): string => {
    const s = lookup('address', value);
    const first = s.split(/, (?=\d)/)[0];
    return /\d{5}/.test(first) ? first : `${first}${c.addressSuffix}`;
  };
  BUILDINGS = rows
    .map((r) => ({
      id: String(r[iId]),
      heat: Number(r[iHeat]) || 0,
      heatKnown: r[iHeat] != null && Number.isFinite(Number(r[iHeat])),
      area: r[fields.indexOf('totalArea')] == null ? undefined : Number(r[fields.indexOf('totalArea')]),
      heatingEnergy: lookup('heatingEnergy', r[fields.indexOf('heatingEnergy')]),
      address: address(r[iAddr]),
      usage: r[iUsage] === null || r[iUsage] === undefined
        ? lookup('usage', r[iUsageCategory])
        : lookup('usageLabel', r[iUsage]),
    }));
}

/** Plus gros consommateurs du périmètre : bâtiments proposés comme déclencheurs / obligatoires. */
export const candidates = (n = 8): BuildingRow[] => BUILDINGS.filter((b) => b.heat > 0).sort((a, b) => b.heat - a.heat).slice(0, n);
export const labelOf = (id: string): string => BUILDINGS.find((b) => b.id === id)?.address ?? id;
export const buildingsByIds = (ids: string[]): BuildingRow[] => {
  const indexed = new Map(BUILDINGS.map((building) => [building.id, building]));
  return ids.map((id) => indexed.get(id)).filter((building): building is BuildingRow => !!building);
};

export const YEARS = Array.from({ length: 19 }, (_, i) => String(2027 + i));

type Meta = 'id' | 'name' | 'updatedAt' | 'version';

type Defaults = {
  demande: Omit<DemandeSet, Meta>;
  trace: Omit<TraceSet, Meta>;
  developpement: Omit<DevSet, Meta>;
  phasage: Omit<PhasageSet, Meta>;
  importance: Omit<ImportanceSet, Meta>;
  pointsDepart: Omit<PointsDepartSet, Meta>;
};

export const DEFAULTS: Defaults = {
  demande: { kind: 'demande', consoCoef: 100, connectionRate: 60, erosion: 0.5 },
  trace: { kind: 'trace', forbiddenRoads: 0, mandatoryRoads: 0, weightedRoads: 0, useExistingNetwork: true },
  developpement: { kind: 'developpement', targetKm: 12, targetMWh: 48000, objective: 'mixte', otherObjective: '' },
  phasage: { kind: 'phasage', kmPerYear: 1.5, mwhPerYear: 6000, clientsPerYear: 12 },
  importance: { kind: 'importance', mandatoryBuildings: [], densification: 'moyenne', priority: 'mixte' },
  pointsDepart: { kind: 'pointsDepart', startPoints: 1, boilerRooms: 'existantes', commissioningYear: '2028', capacityMW: 12, backupYear: '2030', backupCapacityMW: 6 },
};

export function newSet(kind: Family, id: string, name: string, updatedAt: string, patch: Partial<AnySet> = {}): AnySet {
  return { ...DEFAULTS[kind], ...patch, id, name, updatedAt, version: 1 } as AnySet;
}

export function cloneSet<T extends AnySet>(s: T): T {
  if (s.kind === 'importance') return { ...s, mandatoryBuildings: [...s.mandatoryBuildings] } as T;
  return { ...s } as T;
}

const r1 = (n: number) => Math.round(n * 10) / 10;

export interface SetCombo {
  demande: DemandeSet;
  trace: TraceSet;
  developpement: DevSet;
  phasage: PhasageSet;
  importance: ImportanceSet;
  pointsDepart: PointsDepartSet;
}

/** Calcul de plan simulé (non métier) : combine les 6 familles de jeux. */
export function computeResults(plan: Plan, c: SetCombo, skip = 0, selectedBuildingIds?: string[]): PlanResults {
  const share = plan.energy === 'froid' ? 0.35 : 1;
  const candidates = BUILDINGS
    .filter((_, i) => skip === 0 || i % skip !== 0)
    .map((b) => ({ ...b, heat: b.heat * share }))
    .sort((a, b) => b.heat - a.heat);
  const selectedIds = selectedBuildingIds === undefined ? undefined : new Set(selectedBuildingIds);
  const all = selectedIds ? candidates.filter((building) => selectedIds.has(building.id)) : candidates;

  // Bâtiments forcés : clients obligatoires (famille Importance).
  const forcedIds = new Set(c.importance.mandatoryBuildings);
  const forced = all.filter((b) => forcedIds.has(b.id));

  // Longueur : contraintes de tracé (routes interdites / obligatoires / pondérées) ;
  // plusieurs points de départ (famille Points de départ) = réseau plus court.
  const routeFactor = (1 + c.trace.forbiddenRoads * 0.012 + c.trace.mandatoryRoads * 0.008 + c.trace.weightedRoads * 0.006) / (1 + (c.pointsDepart.startPoints - 1) * 0.04);
  const perBuildingKm = 0.085 * routeFactor;

  let count = Math.round((all.length * c.demande.connectionRate) / 100);
  if (c.developpement.objective !== 'mwh') count = Math.min(count, Math.floor(c.developpement.targetKm / perBuildingKm));
  count = Math.max(0, Math.min(count, all.length));

  let connected = [...forced, ...all.filter((b) => !forcedIds.has(b.id))].slice(0, Math.max(count, forced.length));
  if (c.developpement.objective !== 'km') {
    let acc = 0;
    connected = connected.filter((b) => (acc += b.heat) <= c.developpement.targetMWh);
    if (connected.length < forced.length) connected = forced;
  }

  const mwh = connected.reduce((s, b) => s + b.heat, 0) * (c.demande.consoCoef / 100);
  const networkKm = r1(connected.length * perBuildingKm);
  const years = Math.max(1, Math.round(Math.max(
    networkKm / c.phasage.kmPerYear,
    mwh / c.phasage.mwhPerYear,
    connected.length / Math.max(1, c.phasage.clientsPerYear),
  )));
  const y0 = Number(c.pointsDepart.commissioningYear);
  const last = y0 + years;
  return {
    connectedBuildings: connected.length,
    connectedIds: connected.map((b) => b.id),
    networkKm,
    deliveredGWh: r1(mwh / 1000),
    erodedGWh: r1((mwh / 1000) * Math.pow(1 - c.demande.erosion / 100, last - y0)),
    density: networkKm > 0 ? Math.round((mwh / (networkKm * 1000)) * 100) / 100 : 0,
    durationYears: years,
    triggered: forced.length,
  };
}
